import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "../components/Toast";
import { Button } from "../components/Button";
import { TextField } from "../components/TextField";
import { confirmPasswordReset } from "../api/auth";
import { errorMessage } from "../api/client";

/**
 * Public reset-password screen reached from the emailed link (?token=…). Confirms a
 * new password against the one-time token, then sends the user to /login.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const mismatch = confirm.length > 0 && newPassword !== confirm;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirm) {
      toast.error(t("resetPassword.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset({ token, new_password: newPassword });
      toast.success(t("resetPassword.success"));
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-screen">
        <div className="auth-card stack">
          <h1>{t("resetPassword.title")}</h1>
          <p className="muted" style={{ textAlign: "center", marginTop: "-8px" }}>
            {t("resetPassword.invalidLink")}
          </p>
          <p className="faint" style={{ textAlign: "center", margin: 0 }}>
            <Link to="/forgot-password">{t("resetPassword.requestNew")}</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <form className="auth-card stack" onSubmit={onSubmit}>
        <h1>{t("resetPassword.title")}</h1>
        <p className="muted" style={{ textAlign: "center", marginTop: "-8px" }}>
          {t("resetPassword.subtitle")}
        </p>
        <TextField
          label={t("resetPassword.newPassword")}
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder={t("auth.newPasswordPlaceholder")}
          hint={t("auth.passwordHint")}
        />
        <TextField
          label={t("resetPassword.confirmPassword")}
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={t("auth.passwordPlaceholder")}
          error={mismatch ? t("resetPassword.mismatch") : null}
        />
        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          disabled={newPassword.length < 8 || newPassword !== confirm}
        >
          {t("resetPassword.submit")}
        </Button>
        <p className="faint" style={{ textAlign: "center", margin: 0 }}>
          <Link to="/login">{t("forgotPassword.backToLogin")}</Link>
        </p>
      </form>
    </div>
  );
}
